<template>
    <v-container fluid>
        <v-card>
            <v-card-title>
                <v-icon left>mdi-clipboard-text-multiple</v-icon>
                Report di Riordino Automatico
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="generateNewReport" :loading="generating">
                    <v-icon left>mdi-plus</v-icon>
                    Genera Nuovo Report
                </v-btn>
            </v-card-title>

            <v-card-text>
                <v-data-table
                    :headers="headers"
                    :items="reports"
                    :loading="loading"
                    sort-by="generated_date"
                    sort-desc
                    class="elevation-1"
                >
                    <template v-slot:item.report_code="{ item }">
                        <strong>{{ item.report_code }}</strong>
                    </template>

                    <template v-slot:item.generated_date="{ item }">
                        {{ formatDate(item.generated_date) }}
                    </template>

                    <template v-slot:item.analysis_period="{ item }">
                        {{ formatDate(item.analysis_start_date) }} - {{ formatDate(item.analysis_end_date) }}
                    </template>

                    <template v-slot:item.products_to_reorder="{ item }">
                        <v-chip small color="primary">
                            {{ item.products_to_reorder }} / {{ item.total_products }}
                        </v-chip>
                    </template>

                    <template v-slot:item.total_estimated_cost="{ item }">
                        <strong>{{ formatEuro(item.total_estimated_cost) }}</strong>
                    </template>

                    <template v-slot:item.status="{ item }">
                        <v-chip small :color="getStatusColor(item.status)">
                            {{ getStatusLabel(item.status) }}
                        </v-chip>
                    </template>

                    <template v-slot:item.actions="{ item }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn icon small @click="viewReport(item)" v-on="on">
                                    <v-icon small>mdi-eye</v-icon>
                                </v-btn>
                            </template>
                            <span>Visualizza Dettagli</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn icon small @click="exportReport(item)" v-on="on" :loading="item.exporting">
                                    <v-icon small>mdi-microsoft-excel</v-icon>
                                </v-btn>
                            </template>
                            <span>Esporta Excel</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn icon small color="error" @click="deleteReport(item)" v-on="on">
                                    <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                            </template>
                            <span>Elimina Report</span>
                        </v-tooltip>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Dialog for report generation configuration -->
        <v-dialog v-model="generateDialog" max-width="600px">
            <v-card>
                <v-card-title>
                    <v-icon left>mdi-cog</v-icon>
                    Configura Nuovo Report
                </v-card-title>

                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                v-model="generateConfig.period"
                                :items="periodOptions"
                                label="Periodo di Analisi"
                                prepend-icon="mdi-calendar-range"
                                outlined
                                hint="Il periodo su cui calcolare le medie di vendita"
                                persistent-hint
                            ></v-select>
                        </v-col>

                        <v-col cols="12" v-if="generateConfig.period === 'custom'">
                            <v-text-field
                                v-model.number="generateConfig.customDays"
                                type="number"
                                label="Numero di giorni"
                                prepend-icon="mdi-calendar"
                                outlined
                                :rules="[v => v > 0 || 'Deve essere maggiore di 0']"
                                hint="Inserisci il numero di giorni per l'analisi personalizzata"
                                persistent-hint
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12">
                            <v-text-field
                                v-model.number="generateConfig.safetyFactor"
                                type="number"
                                step="0.1"
                                label="Fattore di Sicurezza"
                                prepend-icon="mdi-shield-check"
                                outlined
                                hint="Moltiplicatore per il calcolo delle scorte di sicurezza (default: 1.2)"
                                persistent-hint
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model.number="generateConfig.leadTimeDays"
                                type="number"
                                label="Lead Time (giorni)"
                                prepend-icon="mdi-truck-delivery"
                                outlined
                                :rules="[v => v > 0 || 'Deve essere maggiore di 0']"
                                hint="Giorni di attesa dalla fornitura (default: 7)"
                                persistent-hint
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model.number="generateConfig.reorderPointDays"
                                type="number"
                                label="Giorni di Copertura"
                                prepend-icon="mdi-calendar-clock"
                                outlined
                                :rules="[v => v > 0 || 'Deve essere maggiore di 0']"
                                hint="Giorni di copertura desiderati (default: 30)"
                                persistent-hint
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12">
                            <v-select
                                v-model="generateConfig.ratings"
                                :items="ratingOptions"
                                item-text="text"
                                item-value="value"
                                label="Filtra per Rating ABC/FSN (opzionale)"
                                prepend-icon="mdi-star"
                                outlined
                                multiple
                                chips
                                deletable-chips
                                clearable
                                hint="Lascia vuoto per includere tutti. ABC=Valore (A/B/C), FSN=Rotazione (F/S/N)"
                                persistent-hint
                            >
                                <template v-slot:selection="{ item, index }">
                                    <v-chip
                                        v-if="index < 3"
                                        small
                                        :color="getRatingColor(item.value)"
                                        dark
                                        close
                                        @click:close="removeRating(item.value)"
                                    >
                                        {{ item.value }}
                                    </v-chip>
                                    <span
                                        v-if="index === 3"
                                        class="grey--text text-caption"
                                    >
                                        (+{{ generateConfig.ratings.length - 3 }} altri)
                                    </span>
                                </template>
                            </v-select>
                        </v-col>

                        <v-col cols="12">
                            <v-alert type="info" outlined dense>
                                <div class="text-caption">
                                    <strong>Periodo selezionato:</strong> {{ getPeriodDescription() }}<br>
                                    <strong>Fattore di sicurezza:</strong> {{ generateConfig.safetyFactor }}x<br>
                                    <strong>Lead Time:</strong> {{ generateConfig.leadTimeDays }} giorni<br>
                                    <strong>Giorni di copertura:</strong> {{ generateConfig.reorderPointDays }} giorni<br>
                                    <strong>Rating da includere:</strong> {{ generateConfig.ratings && generateConfig.ratings.length > 0 ? generateConfig.ratings.join(', ') : 'Tutti' }}
                                </div>
                            </v-alert>
                        </v-col>

                        <!-- Calculation Logic Explanation -->
                        <v-col cols="12">
                            <v-expansion-panels flat>
                                <v-expansion-panel>
                                    <v-expansion-panel-header class="pa-0">
                                        <div class="d-flex align-center">
                                            <v-icon small left color="info">mdi-information-outline</v-icon>
                                            <span class="text-caption info--text">Come viene calcolata la quantità da ordinare?</span>
                                        </div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <div class="text-caption">
                                            <p class="mb-2"><strong>Formula base:</strong></p>
                                            <code class="d-block pa-2 mb-3" style="background: #f5f5f5; border-radius: 4px;">
                                                Quantità = (VenditeGiornaliere × GiorniCopertura - StockAttuale) × Moltiplicatore
                                            </code>

                                            <p class="mb-2"><strong>Giorni di copertura per classificazione ABC/FSN:</strong></p>
                                            <v-simple-table dense class="mb-3">
                                                <template v-slot:default>
                                                    <thead>
                                                        <tr>
                                                            <th>Rating</th>
                                                            <th>Giorni</th>
                                                            <th>Descrizione</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr><td><v-chip x-small color="purple" dark>AF</v-chip></td><td>45</td><td>Fast + Alto valore (Stella)</td></tr>
                                                        <tr><td><v-chip x-small color="indigo" dark>BF</v-chip></td><td>38</td><td>Fast + Medio valore</td></tr>
                                                        <tr><td><v-chip x-small color="blue" dark>CF</v-chip></td><td>30</td><td>Fast + Basso valore</td></tr>
                                                        <tr><td><v-chip x-small color="teal" dark>AS</v-chip></td><td>35</td><td>Slow + Alto valore</td></tr>
                                                        <tr><td><v-chip x-small color="green" dark>BS</v-chip></td><td>28</td><td>Slow + Medio valore</td></tr>
                                                        <tr><td><v-chip x-small color="lime darken-2" dark>CS</v-chip></td><td>21</td><td>Slow + Basso valore</td></tr>
                                                        <tr><td><v-chip x-small color="grey" dark>*N</v-chip></td><td>14</td><td>Non-moving (qualsiasi ABC)</td></tr>
                                                        <tr><td><v-chip x-small color="orange" dark>New</v-chip></td><td>30</td><td>Prodotto nuovo (&lt;30 gg)</td></tr>
                                                        <tr><td><v-chip x-small color="amber darken-2" dark>In oss.</v-chip></td><td>30</td><td>In osservazione (30-90 gg)</td></tr>
                                                    </tbody>
                                                </template>
                                            </v-simple-table>

                                            <p class="mb-2"><strong>Moltiplicatori per classificazione:</strong></p>
                                            <div class="d-flex flex-wrap">
                                                <v-chip x-small class="mr-1 mb-1" color="purple" dark>AF: 1.4x</v-chip>
                                                <v-chip x-small class="mr-1 mb-1" color="indigo" dark>BF: 1.2x</v-chip>
                                                <v-chip x-small class="mr-1 mb-1" color="blue" dark>CF: 1.1x</v-chip>
                                                <v-chip x-small class="mr-1 mb-1" color="teal" dark>AS: 1.2x</v-chip>
                                                <v-chip x-small class="mr-1 mb-1" color="green" dark>BS: 1.0x</v-chip>
                                                <v-chip x-small class="mr-1 mb-1" color="lime darken-2" dark>CS: 0.8x</v-chip>
                                            </div>

                                            <v-divider class="my-3"></v-divider>

                                            <p class="mb-1"><strong>Esempio:</strong> Prodotto AF che vende 1 pz/giorno, stock attuale 5 pz</p>
                                            <code class="d-block pa-2" style="background: #f5f5f5; border-radius: 4px;">
                                                (1 × 45 - 5) × 1.4 = 40 × 1.4 = <strong>56 pezzi</strong>
                                            </code>
                                        </div>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="generateDialog = false">Annulla</v-btn>
                    <v-btn
                        color="primary"
                        @click="confirmGenerateReport"
                        :loading="generating"
                    >
                        <v-icon left>mdi-play</v-icon>
                        Genera Report
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>


        <!-- Snackbar for notifications -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
            {{ snackbarText }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar = false">Chiudi</v-btn>
            </template>
        </v-snackbar>
    </v-container>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ReorderReportList',
    data() {
        return {
            reports: [],
            loading: false,
            generating: false,
            generateDialog: false,
            generateConfig: {
                period: '30',
                customDays: 60,
                safetyFactor: 1.2,
                leadTimeDays: 7,
                reorderPointDays: 30,
                ratings: [] // Empty means all ratings
            },
            periodOptions: [
                { text: 'Ultimo mese (30 giorni)', value: '30' },
                { text: 'Ultimi 2 mesi (60 giorni)', value: '60' },
                { text: 'Ultimi 3 mesi (90 giorni)', value: '90' },
                { text: 'Ultimi 6 mesi (180 giorni)', value: '180' },
                { text: 'Ultimo anno (365 giorni)', value: '365' },
                { text: 'Personalizzato', value: 'custom' },
            ],
            snackbar: false,
            snackbarText: '',
            snackbarColor: 'success',
            ratingOptions: [
                { text: 'New - Prodotto Nuovo', value: 'New' },
                { text: 'In osservazione - In valutazione', value: 'In osservazione' },
                { text: 'AF - Alto Valore + Fast', value: 'AF' },
                { text: 'AS - Alto Valore + Slow', value: 'AS' },
                { text: 'AN - Alto Valore + Non-moving', value: 'AN' },
                { text: 'BF - Medio Valore + Fast', value: 'BF' },
                { text: 'BS - Medio Valore + Slow', value: 'BS' },
                { text: 'BN - Medio Valore + Non-moving', value: 'BN' },
                { text: 'CF - Basso Valore + Fast', value: 'CF' },
                { text: 'CS - Basso Valore + Slow', value: 'CS' },
                { text: 'CN - Basso Valore + Non-moving', value: 'CN' },
            ],
            headers: [
                { text: 'Codice Report', value: 'report_code', sortable: true },
                { text: 'Data Generazione', value: 'generated_date', sortable: true },
                { text: 'Periodo Analisi', value: 'analysis_period', sortable: false },
                { text: 'Prodotti', value: 'products_to_reorder', sortable: true },
                { text: 'Costo Totale', value: 'total_estimated_cost', sortable: true },
                { text: 'Stato', value: 'status', sortable: true },
                { text: 'Azioni', value: 'actions', sortable: false, align: 'center' },
            ],
        };
    },
    mounted() {
        this.loadReports();
    },
    methods: {
        async loadReports() {
            this.loading = true;
            try {
                const response = await axios.get('/api/reorder/reports');
                // Handle paginated response
                this.reports = response.data.data || [];
            } catch (error) {
                console.error('Error loading reports:', error);
                this.showSnackbar('Errore nel caricamento dei report', 'error');
            } finally {
                this.loading = false;
            }
        },

        generateNewReport() {
            // Open configuration dialog
            this.generateDialog = true;
        },

        async confirmGenerateReport() {
            this.generating = true;
            try {
                // Determine days for analysis
                let days = this.generateConfig.period === 'custom'
                    ? this.generateConfig.customDays
                    : parseInt(this.generateConfig.period);

                // Call API with parameters
                const params = {
                    days: days,
                    safety_factor: this.generateConfig.safetyFactor,
                    lead_time_days: this.generateConfig.leadTimeDays,
                    reorder_point_days: this.generateConfig.reorderPointDays
                };

                // Add ratings filter if selected
                if (this.generateConfig.ratings && this.generateConfig.ratings.length > 0) {
                    params.ratings = this.generateConfig.ratings;
                }

                await axios.post('/api/reorder/reports/generate', params);

                this.showSnackbar('Report generato con successo!', 'success');
                this.generateDialog = false;
                await this.loadReports();
            } catch (error) {
                console.error('Error generating report:', error);
                const errorMsg = error.response?.data?.message || 'Errore nella generazione del report';
                this.showSnackbar(errorMsg, 'error');
            } finally {
                this.generating = false;
            }
        },

        removeRating(item) {
            const index = this.generateConfig.ratings.indexOf(item);
            if (index >= 0) this.generateConfig.ratings.splice(index, 1);
        },

        getPeriodDescription() {
            if (this.generateConfig.period === 'custom') {
                return `${this.generateConfig.customDays} giorni personalizzati`;
            }
            const option = this.periodOptions.find(o => o.value === this.generateConfig.period);
            return option ? option.text : 'Non specificato';
        },

        viewReport(report) {
            this.$router.push({ name: 'reorder-report-detail', params: { id: report.id } });
        },

        async exportReport(report) {
            this.$set(report, 'exporting', true);
            try {
                // Download all Excel files
                window.location.href = `/api/reorder/reports/${report.id}/excel-all`;
                this.showSnackbar('Download Excel avviato', 'success');
            } catch (error) {
                console.error('Error exporting report:', error);
                this.showSnackbar('Errore nell\'esportazione', 'error');
            } finally {
                this.$set(report, 'exporting', false);
            }
        },

        async deleteReport(report) {
            if (!confirm(`Vuoi eliminare il report ${report.report_code}?`)) return;

            try {
                await axios.delete(`/api/reorder/reports/${report.id}`);
                this.showSnackbar('Report eliminato con successo', 'success');
                await this.loadReports();
            } catch (error) {
                console.error('Error deleting report:', error);
                this.showSnackbar('Errore nell\'eliminazione del report', 'error');
            }
        },

        formatDate(date) {
            if (!date) return '-';
            return new Date(date).toLocaleDateString('it-IT');
        },

        formatEuro(value) {
            if (!value) return '€0,00';
            return new Intl.NumberFormat('it-IT', {
                style: 'currency',
                currency: 'EUR'
            }).format(value);
        },

        getStatusColor(status) {
            const colors = {
                'pending': 'orange',
                'generated': 'blue',
                'exported': 'green',
                'completed': 'green',
            };
            return colors[status] || 'grey';
        },

        getStatusLabel(status) {
            const labels = {
                'pending': 'In Attesa',
                'generated': 'Generato',
                'exported': 'Esportato',
                'completed': 'Completato',
            };
            return labels[status] || status;
        },

        showSnackbar(text, color = 'success') {
            this.snackbarText = text;
            this.snackbarColor = color;
            this.snackbar = true;
        },

        getRatingColor(rating) {
            const colors = {
                'AF': 'green darken-2',
                'AS': 'green lighten-1',
                'AN': 'green lighten-3',
                'BF': 'blue darken-1',
                'BS': 'blue lighten-1',
                'BN': 'blue lighten-3',
                'CF': 'orange darken-1',
                'CS': 'orange lighten-1',
                'CN': 'red darken-1',
                'New': 'blue lighten-1',
                'In osservazione': 'purple lighten-1'
            };
            return colors[rating] || 'grey';
        }
    }
};
</script>

<style scoped>
.v-expansion-panel-header {
    padding: 12px 24px;
}
</style>
